import { containsWideChar } from '@src/utility/strings';
import { getPatch } from 'fast-array-diff';

import type { TranslationViewMode } from './alignedBlocks';

/**
 * PLAT-11650 — pure helpers behind "click a translated phrase and type": the
 * word diff the red-line bubble renders, its summary, the analytics
 * classification, and the caret placement from a click point. No React, no
 * MobX.
 */

export type RedlineToken = {
  text: string;
  kind: 'same' | 'removed' | 'added';
};

/** Consecutive non-`same` tokens folded into one "these → those" hunk. */
export type RedlineHunk = { removed: string[]; added: string[] };

const splitWords = (text: string): string[] =>
  text.split(/\s+/).filter(word => word.length > 0);

/**
 * Word-level diff of the phrase as it is (`before`) against the draft
 * (`after`). Tokens are whitespace-separated words with their punctuation
 * attached, so "like," vs "like" honestly reads as `like, → like`. When neither
 * side carries a space and either holds wide characters, the diff runs per
 * code point so a CJK edit still red-lines.
 */
export function diffWordsForRedline(
  before: string,
  after: string,
): RedlineToken[] {
  const spaceless = !/\s/.test(before) && !/\s/.test(after);
  const byCodePoint =
    spaceless && (containsWideChar(before) || containsWideChar(after));
  const a = byCodePoint ? Array.from(before) : splitWords(before);
  const b = byCodePoint ? Array.from(after) : splitWords(after);

  const tokens: RedlineToken[] = [];
  let ai = 0;
  let bi = 0;
  // `getPatch(a, b)` is the edit script from `a` to `b`, in order; everything
  // between two edits is unchanged and is walked through as `same`.
  for (const item of getPatch(a, b)) {
    if (item.type === 'remove') {
      while (ai < item.oldPos) {
        tokens.push({ text: a[ai], kind: 'same' });
        ai += 1;
        bi += 1;
      }
      for (const text of item.items) tokens.push({ text, kind: 'removed' });
      ai += item.items.length;
    } else {
      while (bi < item.newPos) {
        tokens.push({ text: a[ai], kind: 'same' });
        ai += 1;
        bi += 1;
      }
      for (const text of item.items) tokens.push({ text, kind: 'added' });
      bi += item.items.length;
    }
  }
  while (ai < a.length) {
    tokens.push({ text: a[ai], kind: 'same' });
    ai += 1;
  }
  return tokens;
}

export function summarizeRedline(
  tokens: readonly RedlineToken[],
): RedlineHunk[] {
  const hunks: RedlineHunk[] = [];
  let current: RedlineHunk | null = null;
  for (const token of tokens) {
    if (token.kind === 'same') {
      current = null;
      continue;
    }
    if (!current) {
      current = { removed: [], added: [] };
      hunks.push(current);
    }
    (token.kind === 'removed' ? current.removed : current.added).push(
      token.text,
    );
  }
  return hunks;
}

/** `remove` when every hunk only takes words away; anything typed is a replace. */
export function classifyInlineEdit(
  hunks: readonly RedlineHunk[],
): 'replace' | 'remove' {
  const anyAdded = hunks.some(h => h.added.length > 0);
  return anyAdded || hunks.length === 0 ? 'replace' : 'remove';
}

/** Whitespace-separated tokens; a space-less CJK run counts as one. */
export function countWords(text: string): number {
  return splitWords(text).length;
}

/**
 * What a click on a translated phrase does (PLAT-11650).
 *
 * A **single click** seeks, in every view — the same contract as Transcript &
 * Speakers, where a click places the playhead and leaves the keyboard to the
 * player (Space plays / pauses). Opening the in-place field on a single click
 * moved focus into a contenteditable, so Space typed a space instead; it also
 * turned every "where am I" click into an accidental edit (Fernando, Sep 11).
 * A **double-click** in Paragraph / Segment seeks AND opens the phrase for
 * typing, except on a phrase that cannot be edited. Words view keeps its older
 * contract on either gesture: an edited phrase opens the editor popover, an
 * unedited one seeks.
 */
export function resolveTranslatedPhraseClickAction(input: {
  viewMode: TranslationViewMode;
  editable: boolean;
  isEdited: boolean;
  value: string;
  gesture: 'click' | 'double-click';
}): 'popover' | 'seek' | 'seek-and-edit' {
  if (input.viewMode === 'fragment') {
    return input.isEdited ? 'popover' : 'seek';
  }
  if (input.gesture !== 'double-click') return 'seek';
  if (!input.editable || input.value.trim() === '') return 'seek';
  return 'seek-and-edit';
}

/**
 * PLAT-11650 — whether this browser has LEARNED the double-click: the hint
 * ("Double-click to edit") shows on single clicks until the reviewer has
 * actually double-clicked into a phrase, or used the pencil, once — not until
 * it has merely been displayed once (Fernando, Sep 14: shown-once was too
 * subtle). Storage is a convenience, not a record: a browser that refuses it
 * simply keeps offering the hint, which is the safe way to be wrong.
 */
export const DOUBLE_CLICK_EDIT_HINT_KEY = 'arbor-review-double-click-edit-hint';

/** The most times the hint shows in one page load, learned or not. */
export const HINT_MAX_PER_LOAD = 3;

export function hasLearnedDoubleClickEdit(
  storage: Pick<Storage, 'getItem'> | null = readLocalStorage(),
): boolean {
  try {
    return storage?.getItem(DOUBLE_CLICK_EDIT_HINT_KEY) === 'learned';
  } catch {
    return false;
  }
}

export function markDoubleClickEditLearned(
  storage: Pick<Storage, 'setItem'> | null = readLocalStorage(),
): void {
  try {
    storage?.setItem(DOUBLE_CLICK_EDIT_HINT_KEY, 'learned');
  } catch {
    // A private window or blocked site data: the hint keeps showing.
  }
}

function readLocalStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

type CaretPoint = { node: Node; offset: number } | null;

function caretPointAt(x: number, y: number): CaretPoint {
  const doc = document as Document & {
    caretPositionFromPoint?: (
      x: number,
      y: number,
    ) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  try {
    if (typeof doc.caretPositionFromPoint === 'function') {
      const pos = doc.caretPositionFromPoint(x, y);
      return pos ? { node: pos.offsetNode, offset: pos.offset } : null;
    }
    if (typeof doc.caretRangeFromPoint === 'function') {
      const range = doc.caretRangeFromPoint(x, y);
      return range
        ? { node: range.startContainer, offset: range.startOffset }
        : null;
    }
  } catch {
    // A browser that throws for a point outside the viewport: fall through.
  }
  return null;
}

/**
 * The character offset, into the phrase's text, of the click at (x, y) on the
 * phrase span `el`. The span's `textContent` reproduces the phrase value
 * exactly (its inner marks all emit the same characters), so the offset is the
 * length of every text node before the hit node, plus the hit offset. Falls
 * back to the end of the text when the browser has no caret API (jsdom), or the
 * hit lands outside `el`.
 */
export function resolveCaretOffsetAtPoint(
  el: HTMLElement,
  x: number,
  y: number,
  textLength: number,
): number {
  const hit = caretPointAt(x, y);
  if (!hit || !el.contains(hit.node)) return textLength;
  if (hit.node === el) {
    // Offset counts child nodes: sum the text before that child.
    let total = 0;
    for (let i = 0; i < hit.offset && i < el.childNodes.length; i += 1) {
      total += el.childNodes[i].textContent?.length ?? 0;
    }
    return Math.min(total, textLength);
  }
  let total = 0;
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node && node !== hit.node) {
    total += node.textContent?.length ?? 0;
    node = walker.nextNode();
  }
  return Math.max(0, Math.min(total + hit.offset, textLength));
}
